import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import CustomPagination from "../../../utils/paginationUtils";
import editIcon from "../../../assets/edit-icon.svg";
import eyeIcon from "../../../assets/eye.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import CLOSE from "../../../assets/CLOSE.svg";
import Switch from "@mui/material/Switch";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { ReportServices } from "../../../services/ReportServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import InactiveDocumentModal from "./InactiveDocumentModal";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

export default function ManageDocuments() {
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelType, setPanelType] = useState("add");
  const [currentDoc, setCurrentDoc] = useState({ name: "", documentName: "", status: "" });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [users, setUsers] = useState(null); // userId -> userName map from ReportServices.getUsers()
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [docToInactive, setDocToInactive] = useState(null);
  const [mappedProcedures, setMappedProcedures] = useState([]);
  const [subTypeOptions, setSubTypeOptions] = useState([]);
  const [loadingSubTypes, setLoadingSubTypes] = useState(false);
  const [initialDocForEdit, setInitialDocForEdit] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const searchDebounceRef = useRef(null);

  const getUsersDetails = async () => {
    try {
      const response = await ReportServices.getUsers();
      setUsers(response?.data?.users ?? null);
    } catch (err) {
      handleApiErrorResponses(err);
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ProcedureFlowServices.getDefaultDocumentNameList({
        status: "ACTIVE",
        ...(searchKey.trim() && { searchKey: searchKey.trim() }),
        page,
        limit: PAGE_SIZE,
      });
      const data = res?.data;
      const list = data?.docNameData ?? [];
      setTotalCount(data?.total ?? list.length);
      setDocuments(
        list.map((item) => ({
          id: item.id,
          name: item.documentName,
          documentName: item.documentName,
          documentSubType: item.documentSubType ?? "",
          status: item.status,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          createdBy: item.createdBy,
          updatedBy: item.updatedBy,
        }))
      );
      const pages = data?.totalPages ?? 1;
      setTotalPages(pages > 0 ? pages : 1);
    } catch (err) {
      console.error("Failed to fetch documents", err);
      setError("Failed to load documents. Please try again.");
      setDocuments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [page, searchKey]);

  /** Fetch document sub-type options from master-data-list?type=documentSubType */
  const fetchSubTypeOptions = async () => {
    setLoadingSubTypes(true);
    try {
      const res = await ProcedureFlowServices.getMasterDataList("documentSubType");
      const items = res?.data?.masterData?.allmasterdata || [];
      const subTypes = items
        .map((item) => (typeof item === "string" ? item : item.value ?? "").trim())
        .filter(Boolean);
      setSubTypeOptions(subTypes);
    } catch (err) {
      console.error("Failed to fetch sub-type options:", err);
      setSubTypeOptions([]);
    } finally {
      setLoadingSubTypes(false);
    }
  };

  useEffect(() => {
    getUsersDetails();
    fetchSubTypeOptions();
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchKey(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const handleSave = async () => {
    const docName = (currentDoc?.documentName ?? currentDoc?.name ?? "").trim();
    if (!docName) {
      setSaveError("Document name is required.");
      return;
    }
    const subType = (currentDoc?.documentSubType ?? "").trim();
    if (!subType) {
      setSaveError("Document sub type is required.");
      return;
    }

    if (panelType === "edit" && initialDocForEdit) {
      const initialName = (initialDocForEdit?.documentName ?? initialDocForEdit?.name ?? "").trim();
      const initialSubType = (initialDocForEdit?.documentSubType ?? "").trim();
      if (docName === initialName && subType === initialSubType) {
        setSaveError("No changes to update. Please modify the document details first.");
        return;
      }
    }

    setSaveError(null);
    setSaving(true);
    try {
      if (panelType === "edit") {
        await ProcedureFlowServices.addUpdateDefaultDocName([
          {
            id: currentDoc.id,
            documentName: docName,
            documentSubType: subType || undefined,
            status: currentDoc.status || "ACTIVE",
          },
        ]);
      } else {
        await ProcedureFlowServices.addUpdateDefaultDocName([
          { documentName: docName, documentSubType: subType || undefined },
        ]);
      }
      setPanelVisible(false);
      setPanelType("add");
      setCurrentDoc({ name: "", documentName: "", documentSubType: "", status: "" });
      setInitialDocForEdit(null);
      toast.success(panelType === "edit" ? "Document updated successfully." : "Document added successfully.");
      await fetchDocuments();
    } catch (err) {
      console.error("Failed to save document", err);
      setSaveError(err?.response?.data?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setPanelVisible(true);
    setPanelType("add");
    setCurrentDoc({ name: "", documentName: "", documentSubType: "", status: "" });
    setInitialDocForEdit(null);
    setSaveError(null);
  };

  const openEdit = (doc) => {
    setPanelVisible(true);
    setPanelType("edit");
    const docData = {
      id: doc.id,
      name: doc.name ?? doc.documentName,
      documentName: doc.documentName ?? doc.name,
      documentSubType: doc.documentSubType ?? "",
      status: doc.status,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
      createdBy: doc.createdBy,
      updatedBy: doc.updatedBy,
    };
    setCurrentDoc(docData);
    setInitialDocForEdit(docData);
    setSaveError(null);
  };

  const openView = (doc) => {
    setPanelVisible(true);
    setPanelType("view");
    setCurrentDoc({
      id: doc.id,
      name: doc.name ?? doc.documentName,
      documentName: doc.documentName ?? doc.name,
      documentSubType: doc.documentSubType ?? "",
      status: doc.status,
      createdAt: doc.createdAt,
      updatedAt: doc.updatedAt,
      createdBy: doc.createdBy,
      updatedBy: doc.updatedBy,
    });
  };

  const openInactiveModal = async (doc) => {
    const targetId = doc?.id ?? doc?.documentId;
    setDocToInactive(doc);
    setMappedProcedures([]);
    try {
      const res = await ProcedureFlowServices.getDocumentLinkedProcedures({
        isDocumentTypeId: true,
        targetId,
        page: 1,
        limit: 100,
      });
      const raw = res?.data?.mappedProcedures ?? [];
      const procedures = raw.map((item) => ({
        id: item.id,
        procedureSchemeId: item.procedureSchemeId,
        documentTypeMappingId: item.documentTypeMappingId,
        procedure: item?.proceduresschememaster?.proceduremaster?.procedure ?? item.procedure ?? "",
        procedureId: item?.proceduresschememaster?.procedureId ?? item.procedureId,
      }));
      setMappedProcedures(procedures);
      console.log("document-linked-procedures targetId:", targetId);
      console.log("document-linked-procedures (isDocumentTypeId=true, targetId=" + targetId + ") FULL response:", res);
      console.log("document-linked-procedures res.data:", JSON.stringify(res?.data, null, 2));
    } catch (err) {
      console.log("document-linked-procedures error:", err);
    }
    setInactiveModalOpen(true);
  };

  const closeInactiveModal = () => {
    setInactiveModalOpen(false);
    setDocToInactive(null);
    setMappedProcedures([]);
  };

  const handleConfirmInactive = async (selectedProcedures = []) => {
    console.log("------- handleConfirmInactive -------");
    console.log("doc:", docToInactive);
    console.log("selectedProcedures:", selectedProcedures);

    if (!docToInactive?.id) {
      closeInactiveModal();
      return;
    }

    try {
      // API 1: add-update-default-doc-name
      const docPayload = [{ id: docToInactive.id, status: "INACTIVE" }];
      console.log("------- API 1: add-update-default-doc-name -------");
      console.log("payload:", docPayload);
      const docRes = await ProcedureFlowServices.addUpdateDefaultDocName(docPayload);
      console.log("response:", docRes?.data);
      console.log("----------------------------------------");

      // API 2: update-doctype-procedure-mapping?task=mapping
      if (selectedProcedures.length > 0) {
        const seen = new Set();
        const procPayload = selectedProcedures
          .filter((p) => {
            if (!p?.id || seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
          })
          .map((p) => ({ id: String(p.id), status: "DELETED" }));
        if (procPayload.length > 0) {
          console.log("------- API 2: update-doctype-procedure-mapping?task=mapping -------");
          console.log("payload:", procPayload);
          const procRes = await ProcedureFlowServices.updateDocTypeProcedureMapping(
            procPayload,
            "mapping"
          );
          console.log("response:", procRes?.data);
          console.log("----------------------------------------");
        }
      }

      setDocuments((prev) =>
        prev.map((d) =>
          d.id === docToInactive.id ? { ...d, status: "INACTIVE" } : d
        )
      );
      toast.success("Document inactivated successfully.");
      closeInactiveModal();
      console.log("------- inactive completed -------");
    } catch (err) {
      handleApiErrorResponses(err);
      toast.error("Failed to inactive document. Please try again.");
      console.log("------- handleConfirmInactive error -------", err);
    }
  };

  return (
    <div className="w-full min-h-screen min-h-[100dvh] flex flex-col">
      {/* Figma: Green gradient background for the entire page */}
      <div className="bg-[radial-gradient(81.98%_338.81%_at_13.41%_276.08%,_rgba(90,198,159,0.5)_0%,_rgba(222,222,222,0.5)_100%)] flex-1 min-h-full w-full pt-6 pb-3 px-3 sm:pt-8 sm:pb-4 sm:px-4 md:pt-12 md:pb-6 md:px-6">
        {/* HEADER: Title, Search, Add Button - on green background */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6 py-4 sm:py-6 md:py-8 mb-4 sm:mb-6">
          <h1 className="font-lufga font-normal text-[50px] leading-[100%] tracking-[-0.07em] align-middle text-black shrink-0">
            Manage Documents ({totalCount})
          </h1>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 lg:gap-6 flex-1 lg:flex-initial lg:max-w-[720px]">
            {/* SEARCH: capsule, white, magnifying glass, placeholder Document Name */}
            <div className="relative flex-1 min-w-0">
              <img
                src={searchIcon}
                alt="search"
                className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Document Name"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full h-12 sm:h-14 pl-11 sm:pl-14 pr-5 sm:pr-6 border border-solid border-[#E0E0E0] rounded-[54px] outline-none font-lufga text-sm sm:text-base text-[#1B1B1B] placeholder:text-gray-400 bg-white"
              />
            </div>

            {/* ADD BUTTON: Figma - 237×80, radius 54px, gap 10px, px 24, border 2px, bg #5AC69FCC */}
            <button
              onClick={openAdd}
              className="font-lufga w-[237px] h-[60px] rounded-[54px] gap-[5px] px-6 border-2 border-solid border-[#5AC69F] bg-[#5AC69FCC] flex items-center justify-center font-medium text-black hover:opacity-90 transition-opacity shrink-0"
            >
              <span className="text-xl leading-none">+</span>
              Add New Document
            </button>
          </div>
        </div>

        {/* MAIN AREA: White card for table */}
        <div
          className={`grid gap-6 transition-all duration-300 ${
            panelVisible ? "lg:grid-cols-[1fr_minmax(35vw,40vw)]" : "grid-cols-1"
          }`}
        >
          {/* LEFT: TABLE - White card with rounded corners and shadow */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden p-4 sm:p-6">
            {/* Table header: SNo. | Document Name (bold, dark grey) */}
            <div className="grid grid-cols-[56px_1fr_180px_120px] gap-4 px-0 sm:px-2 py-3 font-lufga font-semibold text-sm sm:text-base text-[#1B1B1B] border-b border-[#E0E0E0]">
              <span>SNo.</span>
              <span>Document Name</span>
              <span>Document Sub Type</span>
              <span className="text-right pr-2">Actions</span>
            </div>

            <div className="font-lufga divide-y divide-[#E8E8E8]">
              {loading ? (
                <div className="py-12 text-center text-gray-500 font-lufga">
                  Loading documents...
                </div>
              ) : error ? (
                <div className="py-12 text-center text-red-600 font-lufga">
                  {error}
                </div>
              ) : documents.length === 0 ? (
                <div className="py-12 text-center text-gray-500 font-lufga">
                  No documents found.
                </div>
              ) : (
                documents.map((doc, index) => (
                  <DocumentRow
                    key={doc.id}
                    index={(page - 1) * PAGE_SIZE + index}
                    doc={doc}
                    onEdit={() => openEdit(doc)}
                    onView={() => openView(doc)}
                    onToggleInactive={() => openInactiveModal(doc)}
                    onReactivate={async () => {
                      try {
                        const res = await ProcedureFlowServices.getDocumentLinkedProcedures({
                          isDocumentTypeId: true,
                          targetId: doc?.id ?? doc?.documentId,
                          page: 1,
                          limit: 100,
                        });
                        console.log("document-linked-procedures response (reactivate):", res.data);
                      } catch (err) {
                        console.log("document-linked-procedures error (reactivate):", err);
                      }
                      setDocuments((prev) =>
                        prev.map((d) =>
                          d.id === doc.id ? { ...d, status: "ACTIVE" } : d
                        )
                      );
                    }}
                    alternateBg={index % 2 === 1}
                  />
                ))
              )}
            </div>

            {/* Pagination: centered, PREVIOUS | 1 2 3 ... 9 10 | NEXT */}
            {!loading && !error && documents.length > 0 && (
              <div className="flex justify-center mt-6 mb-4 sm:mb-6">
                <CustomPagination
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                />
              </div>
            )}
          </div>

          {/* RIGHT PANEL */}
          {panelVisible && (
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-[#E0E0E0] p-6 sm:p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-lufga text-[32px] font-normal">
                  {panelType === "add"
                    ? "Add Document"
                    : panelType === "edit"
                    ? "Edit Document"
                    : "View Document"}
                </h2>
                <img
                  src={CLOSE}
                  alt="close"
                  onClick={() => setPanelVisible(false)}
                  className="w-6 h-6 cursor-pointer"
                />
              </div>

              {/* ADD/EDIT */}
              {(panelType === "add" || panelType === "edit") && (
                <>
                  <div className="relative mb-4">
                    <input
                      type="text"
                      placeholder="Name of the Document"
                      value={currentDoc?.name ?? currentDoc?.documentName ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCurrentDoc({
                          ...currentDoc,
                          name: val,
                          documentName: val,
                        });
                        setSaveError(null);
                      }}
                      className="w-full h-[70px] px-6 pr-40 border-2 border-gray-200 border-solid rounded-[40px] outline-none font-lufga text-[16px]"
                    />
                    {(currentDoc?.name ?? currentDoc?.documentName ?? "").length > 0 && (
                      <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
                        Document Name
                      </span>
                    )}
                  </div>

                  {/* Document Sub Type dropdown */}
                  <SubTypeDropdown
                    value={currentDoc?.documentSubType ?? ""}
                    onChange={(val) => {
                      setCurrentDoc({ ...currentDoc, documentSubType: val });
                      setSaveError(null);
                    }}
                    options={subTypeOptions}
                    loading={loadingSubTypes}
                  />

                  {saveError && (
                    <p className="text-red-600 text-sm font-lufga mb-4">{saveError}</p>
                  )}
                  {(() => {
                    const docName = (currentDoc?.documentName ?? currentDoc?.name ?? "").trim();
                    const subType = (currentDoc?.documentSubType ?? "").trim();
                    const initialName = (initialDocForEdit?.documentName ?? initialDocForEdit?.name ?? "").trim();
                    const initialSubType = (initialDocForEdit?.documentSubType ?? "").trim();
                    const hasChanges = panelType !== "edit" || docName !== initialName || subType !== initialSubType;
                    return (
                      <button
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className="w-full h-[70px] border-2 border-solid border-[#5AC69F] bg-emerald-400 rounded-[40px] font-lufga font-semibold hover:bg-emerald-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {saving ? "Saving..." : panelType === "edit" ? "Update Document" : "Add Document"}
                      </button>
                    );
                  })()}
                </>
              )}

              {/* VIEW: data from API (selected row) */}
              {panelType === "view" && (
                <div className="font-lufga text-gray-700 space-y-3">
                  <div>
                    <strong>Document Name:</strong>{" "}
                    {currentDoc?.documentName ?? currentDoc?.name ?? "—"}
                  </div>
                  <div>
                    <strong>Document Sub Type:</strong>{" "}
                    {currentDoc?.documentSubType || "—"}
                  </div>
                  <div>
                    <strong>Status:</strong>{" "}
                    {currentDoc?.status ?? "—"}
                  </div>
                  <div>
                    <strong>Created On:</strong>{" "}
                    {currentDoc?.createdAt
                      ? new Date(currentDoc.createdAt).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "—"}
                  </div>
                  <div>
                    <strong>Created By:</strong>{" "}
                    {currentDoc?.createdBy
                      ? (users?.[currentDoc.createdBy] ?? currentDoc.createdBy)
                      : "—"}
                  </div>
                  <div>
                    <strong>Updated On:</strong>{" "}
                    {currentDoc?.updatedAt
                      ? new Date(currentDoc.updatedAt).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "—"}
                  </div>
                  <div>
                    <strong>Updated By:</strong>{" "}
                    {currentDoc?.updatedBy
                      ? (users?.[currentDoc.updatedBy] ?? currentDoc.updatedBy)
                      : "—"}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {inactiveModalOpen && (
          <InactiveDocumentModal
            onClose={closeInactiveModal}
            onConfirm={handleConfirmInactive}
            procedures={mappedProcedures}
          />
        )}
      </div>
    </div>
  );
}

/* ROW: SNo. | Document Name (wrapping, dark grey) | eye | edit | toggle (green when active) */
function DocumentRow({ index, doc, onEdit, onView, onToggleInactive, onReactivate, alternateBg }) {
  const isActive = doc?.status === "ACTIVE";
  const [enabled, setEnabled] = useState(isActive);

  useEffect(() => {
    setEnabled(isActive);
  }, [isActive]);

  const handleToggle = () => {
    if (enabled) {
      onToggleInactive?.();
    } else {
      onReactivate?.();
      setEnabled(true);
    }
  };

  return (
    <div
      className={`grid grid-cols-[56px_1fr_180px_120px] gap-4 px-0 sm:px-2 py-2 sm:py-3 items-center font-lufga ${
        alternateBg ? "bg-[#E8E8E833]" : ""
      }`}
    >
      <span className="font-medium text-sm sm:text-base text-[#1B1B1B]">
        {index + 1}
      </span>

      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {doc.documentName ?? doc.name}
      </p>

      <p className="text-sm sm:text-base text-gray-600 leading-relaxed break-words min-w-0">
        {doc.documentSubType || "—"}
      </p>

      <div className="flex items-center gap-3 sm:gap-4 justify-end pr-2">
        <img
          src={eyeIcon}
          alt="view"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onView}
        />
        <img
          src={editIcon}
          alt="edit"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onEdit}
        />
        <Switch
          checked={enabled}
          onChange={handleToggle}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#5AC69F",
              "& + .MuiSwitch-track": { backgroundColor: "#5AC69F !important", opacity: 0.6 },
            },
            "& .MuiSwitch-track": {
              backgroundColor: "#9e9e9e !important",
            },
          }}
        />
      </div>
    </div>
  );
}

/* ================= DOCUMENT SUB TYPE DROPDOWN ================= */
function SubTypeDropdown({ value, onChange, options = [], loading = false }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const ref = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
    if (!open) { setSearchQuery(""); setHighlightedIndex(-1); }
  }, [open]);

  const filteredOptions = options.filter((opt) =>
    String(opt).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div ref={ref} className="relative w-full mb-4">
      <div
        onClick={() => !loading && setOpen(!open)}
        className={`flex items-center gap-3 h-[70px] px-6 border-2 border-gray-200 border-solid rounded-[40px] font-lufga text-[16px] bg-white ${
          loading ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        }`}
      >
        <span className={`flex-1 min-w-0 truncate ${value ? "text-[#1B1B1B]" : "text-gray-400"}`}>
          {loading ? "Loading sub types..." : value || "Select Document Sub Type"}
        </span>
        {value && !loading && (
          <span className="font-lufga text-[13px] text-gray-400 pointer-events-none shrink-0">
            Document Sub Type
          </span>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {value && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
                setOpen(false);
              }}
              className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors text-xs font-bold cursor-pointer"
              title="Clear selection"
            >
              &#x2715;
            </span>
          )}
          <svg
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          >
            <path
              d="M1 1L7 7L13 1"
              stroke="#9CA3AF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg max-h-[280px] flex flex-col overflow-hidden border border-[#E8E8E8]">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-2 shrink-0">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setHighlightedIndex(-1); }}
              onKeyDown={(e) => {
                if (filteredOptions.length === 0) return;
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setHighlightedIndex((prev) => prev < filteredOptions.length - 1 ? prev + 1 : 0);
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setHighlightedIndex((prev) => prev > 0 ? prev - 1 : filteredOptions.length - 1);
                } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                  e.preventDefault();
                  const opt = filteredOptions[highlightedIndex];
                  onChange(opt === value ? "" : opt);
                  setOpen(false);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search document sub type..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-lufga outline-none focus:ring-2 focus:ring-[#5AC69F] focus:border-transparent"
            />
          </div>
          <div className="overflow-y-auto flex-1 max-h-[220px]">
            {options.length === 0 ? (
              <div className="px-6 py-3 text-sm text-gray-400 font-lufga">
                No sub types available
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-6 py-3 text-sm text-gray-400 font-lufga">
                No results found
              </div>
            ) : (
              filteredOptions.map((opt, idx) => {
                const isSelected = opt === value;
                const isHighlighted = highlightedIndex === idx;
                return (
                  <div
                    key={`${opt}-${idx}`}
                    ref={isHighlighted ? (el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" }) : undefined}
                    onClick={() => {
                      onChange(isSelected ? "" : opt);
                      setOpen(false);
                    }}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`px-6 py-3 text-sm font-lufga cursor-pointer flex items-center justify-between ${
                      isSelected
                        ? "bg-[#E8F5E9] text-[#2E7D32] font-medium hover:bg-[#C8E6C9]"
                        : isHighlighted
                          ? "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F]"
                          : "hover:bg-[#F4FBF8] text-[#1B1B1B]"
                    }`}
                  >
                    <span>{opt}</span>
                    {isSelected && (
                      <span className="text-xs text-[#2E7D32]">&#x2715;</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
